import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  TradingAccountCard,
  TradingCalendarCard,
  TradingPnlChart,
  TradingStatsCard,
  TradingPnlCalendar,
  TradingPnlStatsCard,
  TradingProductivityStatsCard,
} from "./components";
import {
  DATE_FILTERS,
  useTradingDateFilter,
} from "@utils/getDateRangeByFilter";
import axios from "@helpers/axios/private.axios";
import { WALLET_TYPE } from "@constants";
import "./index.sass";

export const ACCOUNT_TYPES = {
  REAL: "real",
  DEMO: "demo",
};

export const PNL_CALENDAR_MODES = {
  DAILY: "daily",
  MONTHLY: "monthly",
};

export const PERFORMANCE_FILTERS = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  LAST_7_DAYS: "last_7_days",
  LAST_30_DAYS: "last_30_days",
  CURRENT_MONTH: "current_month",
  PREVIOUS_MONTH: "previous_month",
  ALL_TIME: "all_time",
};

const tradingStatsFallback = {
  totalTrades: 0,
  winRate: 0,
  wins: 0,
  losses: 0,
  tradedVolume: 0,
  realizedPnl: 0,
};

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;

const formatDate = (date) => date.toISOString().split("T")[0];

const getAccountTypeByWalletType = (walletType) => {
  switch (walletType) {
    case WALLET_TYPE.REAL_FUTURES:
      return ACCOUNT_TYPES.REAL;

    case WALLET_TYPE.DEMO_FUTURES:
      return ACCOUNT_TYPES.DEMO;

    default:
      return null;
  }
};

const getPerformanceDateRange = (filter) => {
  const dateTo = new Date();
  const dateFrom = new Date();

  switch (filter) {
    case PERFORMANCE_FILTERS.YESTERDAY:
      dateFrom.setDate(dateTo.getDate() - 1);
      dateTo.setDate(dateTo.getDate() - 1);
      break;

    case PERFORMANCE_FILTERS.LAST_7_DAYS:
      dateFrom.setDate(dateTo.getDate() - 6);
      break;

    case PERFORMANCE_FILTERS.LAST_30_DAYS:
      dateFrom.setDate(dateTo.getDate() - 29);
      break;

    case PERFORMANCE_FILTERS.CURRENT_MONTH:
      dateFrom.setDate(1);
      break;

    case PERFORMANCE_FILTERS.PREVIOUS_MONTH:
      dateFrom.setMonth(dateTo.getMonth() - 1);
      dateFrom.setDate(1);
      dateTo.setDate(0);
      break;

    case PERFORMANCE_FILTERS.ALL_TIME:
      return {
        dateFrom: "2026-01-01",
        dateTo: formatDate(new Date()),
      };

    case PERFORMANCE_FILTERS.TODAY:
    default:
      break;
  }

  return {
    dateFrom: formatDate(dateFrom),
    dateTo: formatDate(dateTo),
  };
};

export const TradingProfileTab = () => {
  const activeWalletType = useSelector(
    (state) => state.wallet?.active_wallet?.type,
  );

  const activeWalletAccount = useMemo(
    () => getAccountTypeByWalletType(activeWalletType),
    [activeWalletType],
  );

  const [selectedAccount, setSelectedAccount] = useState(
    () => activeWalletAccount ?? ACCOUNT_TYPES.REAL,
  );

  const [tradingProfileData, setTradingProfileData] = useState(null);
  const [isTradingProfileLoading, setIsTradingProfileLoading] = useState(false);
  const [tradingProfileError, setTradingProfileError] = useState(null);

  const [pnlCalendarMode, setPnlCalendarMode] = useState(
    PNL_CALENDAR_MODES.DAILY,
  );
  const [pnlCalendarYear, setPnlCalendarYear] = useState(currentYear);
  const [pnlCalendarMonth, setPnlCalendarMonth] = useState(currentMonth);
  const [pnlCalendarData, setPnlCalendarData] = useState(null);
  const [isPnlCalendarLoading, setIsPnlCalendarLoading] = useState(false);
  const [pnlCalendarError, setPnlCalendarError] = useState(null);

  const [performanceFilter, setPerformanceFilter] = useState(
    PERFORMANCE_FILTERS.TODAY,
  );
  const [performanceStatsData, setPerformanceStatsData] = useState(null);
  const [isPerformanceStatsLoading, setIsPerformanceStatsLoading] =
    useState(false);
  const [performanceStatsError, setPerformanceStatsError] = useState(null);

  const {
    selectedDateFilter,
    setSelectedDateFilter,
    customRange,
    setCustomRange,
    dateRange,
    dateFilterLabels,
  } = useTradingDateFilter();

  const performanceDateRange = useMemo(() => {
    return getPerformanceDateRange(performanceFilter);
  }, [performanceFilter]);

  const handleCustomRangeApply = (range) => {
    setCustomRange(range);
    setSelectedDateFilter(DATE_FILTERS.CUSTOM_RANGE);
  };

  const handleDateFilterChange = (filter) => {
    setSelectedDateFilter(filter);

    if (filter !== DATE_FILTERS.CUSTOM_RANGE) {
      setCustomRange({
        dateFrom: null,
        dateTo: null,
      });
    }
  };

  useEffect(() => {
    if (!activeWalletAccount || activeWalletAccount === selectedAccount) {
      return;
    }

    setSelectedAccount(activeWalletAccount);

    setSelectedDateFilter(DATE_FILTERS.TODAY);
    setCustomRange({
      dateFrom: null,
      dateTo: null,
    });

    setPnlCalendarMode(PNL_CALENDAR_MODES.DAILY);
    setPnlCalendarYear(currentYear);
    setPnlCalendarMonth(currentMonth);
    setPerformanceFilter(PERFORMANCE_FILTERS.TODAY);
  }, [
    activeWalletAccount,
    selectedAccount,
    setSelectedDateFilter,
    setCustomRange,
  ]);

  useEffect(() => {
    if (!dateRange?.dateFrom || !dateRange?.dateTo) {
      return;
    }

    const controller = new AbortController();

    const fetchTradingProfileData = async () => {
      try {
        setIsTradingProfileLoading(true);
        setTradingProfileError(null);

        const response = await axios.post(
          "/users/statistics/trading-profile",
          {
            accountType: selectedAccount,
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo,
          },
          {
            signal: controller.signal,
          },
        );

        setTradingProfileData(response.data.data);
      } catch (error) {
        if (error.name !== "CanceledError") {
          setTradingProfileError(error);
        }
      } finally {
        setIsTradingProfileLoading(false);
      }
    };

    fetchTradingProfileData();

    return () => controller.abort();
  }, [selectedAccount, dateRange?.dateFrom, dateRange?.dateTo]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTradingPnlCalendarData = async () => {
      try {
        setIsPnlCalendarLoading(true);
        setPnlCalendarError(null);

        const payload = {
          accountType: selectedAccount,
          mode: pnlCalendarMode,
          year: pnlCalendarYear,
        };

        if (pnlCalendarMode === PNL_CALENDAR_MODES.DAILY) {
          payload.month = pnlCalendarMonth;
        }

        const response = await axios.post(
          "/users/statistics/trading-calendar",
          payload,
          {
            signal: controller.signal,
          },
        );

        setPnlCalendarData(response.data.data);
      } catch (error) {
        if (error.name !== "CanceledError") {
          setPnlCalendarError(error);
        }
      } finally {
        setIsPnlCalendarLoading(false);
      }
    };

    fetchTradingPnlCalendarData();

    return () => controller.abort();
  }, [selectedAccount, pnlCalendarMode, pnlCalendarYear, pnlCalendarMonth]);

  useEffect(() => {
    if (!performanceDateRange.dateFrom || !performanceDateRange.dateTo) {
      return;
    }

    const controller = new AbortController();

    const fetchPerformanceStatsData = async () => {
      try {
        setIsPerformanceStatsLoading(true);
        setPerformanceStatsError(null);

        const response = await axios.post(
          "/users/statistics/performance",
          {
            accountType: selectedAccount,
            dateFrom: performanceDateRange.dateFrom,
            dateTo: performanceDateRange.dateTo,
          },
          {
            signal: controller.signal,
          },
        );

        setPerformanceStatsData(response.data.data);
      } catch (error) {
        if (error.name !== "CanceledError") {
          setPerformanceStatsError(error);
        }
      } finally {
        setIsPerformanceStatsLoading(false);
      }
    };

    fetchPerformanceStatsData();

    return () => controller.abort();
  }, [
    selectedAccount,
    performanceDateRange.dateFrom,
    performanceDateRange.dateTo,
  ]);

  const tradingStats = tradingProfileData?.stats
    ? {
        totalTrades: tradingProfileData.stats.totalTrades ?? 0,
        winRate: tradingProfileData.stats.winRate ?? 0,
        wins: tradingProfileData.stats.wins ?? 0,
        losses: tradingProfileData.stats.losses ?? 0,
        tradedVolume: tradingProfileData.stats.tradingVolume ?? 0,
        realizedPnl: tradingProfileData.stats.realizedPnl ?? 0,
      }
    : tradingStatsFallback;

  return (
    <div className="trading-profile__wrapper">
      <div className="trading-profile__top">
        <div className="trading-profile__top-left">
          <TradingAccountCard />

          <TradingCalendarCard
            selectedDateFilter={selectedDateFilter}
            dateRange={dateRange}
            dateFilterLabels={dateFilterLabels}
            customRange={customRange}
            onDateFilterChange={handleDateFilterChange}
            onCustomRangeApply={handleCustomRangeApply}
          />

          <TradingStatsCard
            stats={tradingStats}
            isLoading={isTradingProfileLoading}
            error={tradingProfileError}
          />
        </div>

        <TradingPnlChart
          selectedAccount={selectedAccount}
          dateRange={dateRange}
          data={tradingProfileData?.pnlChart}
          tradingStats={tradingStats}
          isLoading={isTradingProfileLoading}
        />
      </div>

      <div className="trading-profile__center">
        <div className="trading-profile__calendar-column">
          <TradingPnlCalendar
            mode={pnlCalendarMode}
            year={pnlCalendarYear}
            month={pnlCalendarMonth}
            data={pnlCalendarData}
            isLoading={isPnlCalendarLoading}
            error={pnlCalendarError}
            onModeChange={setPnlCalendarMode}
            onYearChange={setPnlCalendarYear}
            onMonthChange={setPnlCalendarMonth}
          />
        </div>

        <div className="trading-profile__pnl-stats-column">
          <TradingPnlStatsCard
            mode={pnlCalendarMode}
            stats={pnlCalendarData}
            isLoading={isPnlCalendarLoading}
            error={pnlCalendarError}
          />
        </div>
      </div>

      <TradingProductivityStatsCard
        selectedFilter={performanceFilter}
        stats={performanceStatsData}
        isLoading={isPerformanceStatsLoading}
        error={performanceStatsError}
        onFilterChange={setPerformanceFilter}
      />
    </div>
  );
};

export default TradingProfileTab;
